import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  CreateProductUSADto,
  GetProductUSAByCompanyResponseDto,
  GetProductUSAQueryDto,
  GetProductUSAResponseDto,
  ProductUSA,
  UpdateProductUSADto,
} from "@/types/api/api-types";

//#region Get

const getAllProductsUsa = async (query?: GetProductUSAQueryDto) => {
  const response = await axiosApi.get<GetProductUSAResponseDto>("v1/product-usa", {
    params: query,
  });
  return response.data;
};

export const useGetAllProductsUsa = (query?: GetProductUSAQueryDto) => {
  return useQuery({
    queryKey: ["productUsa", "all", query],
    queryFn: () => getAllProductsUsa(query),
  });
};

const getAllProductsUsaByCompany = async (companyId?: string, importerId?: string) => {
  const response = await axiosApi.get<GetProductUSAByCompanyResponseDto>(
    `v1/product-usa/company/${companyId}`,
    {
      params: {
        importerId,
      },
    },
  );
  return response.data;
};

export const useGetAllProductsUsaByCompany = (companyId?: string, importerId?: string) => {
  return useQuery({
    queryKey: ["productUsa", "all", "company", companyId, importerId],
    queryFn: () => getAllProductsUsaByCompany(companyId, importerId),
    enabled: !!companyId && !!importerId,
  });
};

//#endregion

//#region Post

const createProductUsa = async (payload: CreateProductUSADto) => {
  return axiosApi.post<ProductUSA>("v1/product-usa", payload);
};

export const useCreateProductUsa = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProductUsa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productUsa", "all"] });
      toast.success(t("productUsaApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("productUsaApi.createError"));
    },
  });
};

//#endregion

//#region Patch

const updateProductUsa = async ({ id, payload }: { id: string; payload: UpdateProductUSADto }) => {
  return axiosApi.patch<ProductUSA>(`v1/product-usa/${id}`, payload);
};

export const useUpdateProductUsa = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProductUsa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productUsa", "all"] });
      toast.success(t("productUsaApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("productUsaApi.updateError"));
    },
  });
};

//#endregion

//#region Delete

const deleteProductUsa = async (id: string) => {
  return axiosApi.delete(`v1/product-usa/${id}`);
};

export const useDeleteProductUsa = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProductUsa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productUsa", "all"] });
      toast.success(t("productUsaApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("productUsaApi.deleteError"));
    },
  });
};

//#endregion
